import { useEffect } from 'react'
import { api } from '../services/api'
import { useAuthStore } from '../stores/authStore'

// Access tokens live 24h (see API signIn). Refresh well inside that window so an
// active session never expires mid-use. If the stored token is already expired,
// the refresh call returns 401 and the axios interceptor logs the user out and
// redirects to login -- the graceful path, instead of a stuck "no permission".
const REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000 // 6 hours

/**
 * Keeps the access token fresh for authenticated, non-impersonated sessions.
 * Mounted once near the app root.
 */
export function useSessionRefresh() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const impersonating = useAuthStore((s) => s.impersonation.active)

  useEffect(() => {
    // Do not refresh while impersonating: that session uses a separate vendor
    // token and refreshing would drop the impersonation context needed to
    // restore the admin's original session.
    if (!isAuthenticated || impersonating) return

    let cancelled = false

    const refresh = async () => {
      try {
        const res = await api.auth.refreshToken()
        if (cancelled) return
        const { user, token } = res.data.data
        const mappedUser = {
          id: user.userId,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`.trim(),
          role: user.role,
          vendorId: user.tenantType === 'vendor' ? user.tenantId : undefined,
          subscriptionTier: user.subscriptionTier,
        }
        useAuthStore.getState().setAuth(mappedUser, token)
      } catch {
        // 401 is handled by the axios interceptor (logout + redirect). Any other
        // failure is non-fatal: the existing token remains valid until expiry.
      }
    }

    // Refresh on mount (covers reload / return within the token window), then
    // periodically while the tab stays open.
    refresh()
    const id = window.setInterval(refresh, REFRESH_INTERVAL_MS)

    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [isAuthenticated, impersonating])
}
